import React, { useEffect, useState } from "react";
import axios from "../../../utills/api";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Input,
  Row,
  Col,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Container,
} from "reactstrap";
import { toast } from "react-toastify";
import TableContainer from "../../../Components/Common/TableContainer";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import RoomTypeDetails from "./ManageRoomType/RoomTypeDetails";
import RoomTypeTable from "./ManageRoomType/RoomTypeTable";

/* =========================
   EMPTY FORM
========================= */
const emptyForm = {
  name: "",
  code: "",
  description: "",

  maxAdults: 2,
  maxChildren: 0,
  maxOccupancy: 2,

  bedType: "King",
  roomSize: "",

  basePrice: "",

  amenities: "",
  images: "",

  otaRoomCode: "",
  viewType: "",

  extraBedAllowed: false,
  extraBedPrice: 0,

  // NEW
  breakfastIncluded: false,
  breakfastType: "NONE",

  freeCancellation: false,
  freeCancellationHours: 0,

  cancellationPolicy: "",
  termsAndConditions: "",
  paymentPolicy: "",

  checkInTime: "14:00",
  checkOutTime: "12:00",

  status: "ACTIVE",
};

const ManageRoomTypes = () => {
  document.title = "HMS | Room Types";

  const [list, setList] = useState([]);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedRoomType, setSelectedRoomType] = useState(null);

  const [form, setForm] = useState(emptyForm);

  /* =========================
     LOAD ROOM TYPES
  ========================= */
  const fetchRoomTypes = async () => {
    try {
      const res = await axios.get("/manage/room-types");

      const data = res.data || [];

      setList(data);

      if (data.length) {
        setSelectedRoomType((prev) => prev || data[0]);
      }
    } catch (err) {
      toast.error("Failed to load room types");
    }
  };

  useEffect(() => {
    fetchRoomTypes();
  }, []);

  /* =========================
     INPUT HANDLER
  ========================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  /* =========================
     OPEN CREATE
  ========================= */
  const openCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    setModalOpen(true);
  };

  /* =========================
     EDIT
  ========================= */
  const handleEdit = (item) => {
    setForm({
      ...item,
      amenities: item.amenities?.join(", ") || "",
      images: item.images?.join(", ") || "",
    });
    setEditingId(item._id);
    setModalOpen(true);
  };

  /* =========================
     SUBMIT
  ========================= */
  const handleSubmit = async () => {
    try {
      const payload = {
        ...form,
        amenities: form.amenities
          ? form.amenities.split(",").map((a) => a.trim())
          : [],
        images: form.images
          ? form.images.split(",").map((i) => i.trim())
          : [],
      };

      if (editingId) {
        await axios.put(`/manage/room-types/${editingId}`, payload);
        toast.success("Room type updated");
      } else {
        await axios.post("/manage/room-types", payload);
        toast.success("Room type created");
      }

      setModalOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      fetchRoomTypes();
    } catch (err) {
      toast.error("Operation failed");
    }
  };

  /* =========================
     DELETE
  ========================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this room type?")) return;

    try {
      await axios.delete(`/manage/room-types/${id}`);
      toast.success("Deleted");
      fetchRoomTypes();
    } catch {
      toast.error("Delete failed");
    }
  };

  /* =========================
     FILTER
  ========================= */
  const filtered = list.filter((r) =>
    (r.name || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Room Types" pageTitle="Hotel" />

        <Row>
          <Col xxl={12}>
            <Card>
              <CardBody className="d-flex justify-content-between align-items-center">
                <Input
                  placeholder="Search room type..."
                  value={search}
                  style={{ maxWidth: "300px" }}
                  onChange={(e) => setSearch(e.target.value)}
                />

                <div>
                  <Button color="primary"
                    className="me-2" onClick={openCreate}>
                    + Add Room Type
                  </Button>

                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col lg={8}>
            <RoomTypeTable
              data={filtered}
              search={search}
              setSearch={setSearch}
              onSelect={setSelectedRoomType}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAdd={openCreate}
            />
          </Col>

          <Col lg={4}>
            <RoomTypeDetails
              roomType={selectedRoomType}
            />
          </Col>
        </Row>

        {/* ================= MODAL ================= */}
     <Modal
  isOpen={modalOpen}
  toggle={() => setModalOpen(!modalOpen)}
  size="lg"
>
  <ModalHeader>
    {editingId ? "Edit Room Type" : "Add Room Type"}
  </ModalHeader>

  <ModalBody>
    <Form>
      <Row>

        {/* ================= BASIC ================= */}

        <Col md={6}>
          <FormGroup>
            <Label>Name</Label>
            <Input
              name="name"
              value={form.name}
              onChange={handleChange}
            />
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup>
            <Label>Code</Label>
            <Input
              name="code"
              value={form.code}
              onChange={handleChange}
            />
          </FormGroup>
        </Col>

        <Col md={12}>
          <FormGroup>
            <Label>Description</Label>
            <Input
              type="textarea"
              name="description"
              value={form.description}
              onChange={handleChange}
            />
          </FormGroup>
        </Col>

        {/* ================= OCCUPANCY ================= */}

        <Col md={4}>
          <FormGroup>
            <Label>Max Adults</Label>
            <Input
              type="number"
              name="maxAdults"
              value={form.maxAdults}
              onChange={handleChange}
            />
          </FormGroup>
        </Col>

        <Col md={4}>
          <FormGroup>
            <Label>Max Children</Label>
            <Input
              type="number"
              name="maxChildren"
              value={form.maxChildren}
              onChange={handleChange}
            />
          </FormGroup>
        </Col>

        <Col md={4}>
          <FormGroup>
            <Label>Max Occupancy</Label>
            <Input
              type="number"
              name="maxOccupancy"
              value={form.maxOccupancy}
              onChange={handleChange}
            />
          </FormGroup>
        </Col>

        {/* ================= ROOM DETAILS ================= */}

        <Col md={6}>
          <FormGroup>
            <Label>Bed Type</Label>

            <Input
              type="select"
              name="bedType"
              value={form.bedType}
              onChange={handleChange}
            >
              {[
                "King",
                "Queen",
                "Twin",
                "Double",
                "Single",
                "Sofa Bed",
              ].map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </Input>
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup>
            <Label>Size (sqft)</Label>
            <Input
              type="number"
              name="roomSize"
              value={form.roomSize}
              onChange={handleChange}
            />
          </FormGroup>
        </Col>

        {/* ================= PRICING ================= */}

        <Col md={6}>
          <FormGroup>
            <Label>Base Price</Label>
            <Input
              type="number"
              name="basePrice"
              value={form.basePrice}
              onChange={handleChange}
            />
          </FormGroup>
        </Col>

 <Col md={6}>
  <FormGroup>
    <Label>Inventory</Label>
    <Input
      type="number"
      name="inventory"
      value={form.inventory}
      onChange={handleChange}
    />
  </FormGroup>
</Col>

        {/* ================= AMENITIES ================= */}

        <Col md={12}>
          <FormGroup>
            <Label>Amenities (comma separated)</Label>
            <Input
              name="amenities"
              value={form.amenities}
              onChange={handleChange}
              placeholder="WiFi, TV, AC, Mini Bar"
            />
          </FormGroup>
        </Col>

        <Col md={12}>
          <FormGroup>
            <Label>Images (comma-separated URLs)</Label>
            <Input
              name="images"
              value={form.images}
              onChange={handleChange}
            />
          </FormGroup>
        </Col>

        {/* ================= OTA / VIEW ================= */}

        <Col md={6}>
          <FormGroup>
            <Label>OTA Code</Label>
            <Input
              name="otaRoomCode"
              value={form.otaRoomCode}
              onChange={handleChange}
            />
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup>
            <Label>View Type</Label>
            <Input
              name="viewType"
              value={form.viewType}
              onChange={handleChange}
              placeholder="Pool View"
            />
          </FormGroup>
        </Col>

        {/* ================= EXTRA BED ================= */}

        <Col md={6}>
          <FormGroup>
            <Label>Extra Bed Allowed</Label>

            <Input
              type="select"
              name="extraBedAllowed"
              value={String(form.extraBedAllowed)}
              onChange={(e) =>
                setForm({
                  ...form,
                  extraBedAllowed: e.target.value === "true",
                })
              }
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Input>
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup>
            <Label>Extra Bed Price</Label>

            <Input
              type="number"
              name="extraBedPrice"
              value={form.extraBedPrice}
              onChange={handleChange}
              disabled={!form.extraBedAllowed}
            />
          </FormGroup>
        </Col>

        {/* ================= BREAKFAST ================= */}

        <Col md={6}>
          <FormGroup>
            <Label>Breakfast Included</Label>

            <Input
              type="select"
              name="breakfastIncluded"
              value={String(form.breakfastIncluded)}
              onChange={(e) =>
                setForm({
                  ...form,
                  breakfastIncluded:
                    e.target.value === "true",

                  breakfastType:
                    e.target.value === "true"
                      ? form.breakfastType || "BOTH"
                      : "NONE",
                })
              }
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Input>
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup>
            <Label>Breakfast Type</Label>

            <Input
              type="select"
              name="breakfastType"
              value={form.breakfastType}
              onChange={handleChange}
              disabled={!form.breakfastIncluded}
            >
              <option value="NONE">None</option>
              <option value="VEG">Vegetarian</option>
              <option value="NON_VEG">Non-Vegetarian</option>
              <option value="BOTH">Both</option>
            </Input>
          </FormGroup>
        </Col>

        {/* ================= CANCELLATION ================= */}

        <Col md={6}>
          <FormGroup>
            <Label>Free Cancellation</Label>

            <Input
              type="select"
              name="freeCancellation"
              value={String(form.freeCancellation)}
              onChange={(e) =>
                setForm({
                  ...form,
                  freeCancellation:
                    e.target.value === "true",

                  freeCancellationHours:
                    e.target.value === "true"
                      ? form.freeCancellationHours
                      : 0,
                })
              }
            >
              <option value="true">Available</option>
              <option value="false">Not Available</option>
            </Input>
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup>
            <Label>Free Cancellation Before (Hours)</Label>

            <Input
              type="number"
              name="freeCancellationHours"
              value={form.freeCancellationHours}
              onChange={handleChange}
              disabled={!form.freeCancellation}
              placeholder="24"
            />
          </FormGroup>
        </Col>

        <Col md={12}>
          <FormGroup>
            <Label>Cancellation Policy</Label>

            <Input
              type="textarea"
              name="cancellationPolicy"
              value={form.cancellationPolicy}
              onChange={handleChange}
              placeholder="Example: Free cancellation up to 24 hours before check-in. After that, one night charge applies."
              rows="3"
            />
          </FormGroup>
        </Col>

        {/* ================= TERMS ================= */}

        <Col md={12}>
          <FormGroup>
            <Label>Terms & Conditions</Label>

            <Input
              type="textarea"
              name="termsAndConditions"
              value={form.termsAndConditions}
              onChange={handleChange}
              placeholder="Enter room booking terms and conditions..."
              rows="4"
            />
          </FormGroup>
        </Col>

        {/* ================= PAYMENT ================= */}

        <Col md={12}>
          <FormGroup>
            <Label>Payment Policy</Label>

            <Input
              type="textarea"
              name="paymentPolicy"
              value={form.paymentPolicy}
              onChange={handleChange}
              placeholder="Example: 50% advance payment required to confirm booking."
              rows="3"
            />
          </FormGroup>
        </Col>

        {/* ================= CHECK-IN ================= */}

        <Col md={3}>
          <FormGroup>
            <Label>Check In</Label>

            <Input
              type="time"
              name="checkInTime"
              value={form.checkInTime}
              onChange={handleChange}
            />
          </FormGroup>
        </Col>

        <Col md={3}>
          <FormGroup>
            <Label>Check Out</Label>

            <Input
              type="time"
              name="checkOutTime"
              value={form.checkOutTime}
              onChange={handleChange}
            />
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup>
            <Label>Status</Label>

            <Input
              type="select"
              name="status"
              value={form.status}
              onChange={handleChange}
            >
              <option value="ACTIVE">
                ACTIVE
              </option>

              <option value="INACTIVE">
                INACTIVE
              </option>
            </Input>
          </FormGroup>
        </Col>

      </Row>
    </Form>
  </ModalBody>

  <ModalFooter>
    <Button
      color="primary"
      onClick={handleSubmit}
    >
      {editingId ? "Update" : "Create"}
    </Button>

    <Button
      onClick={() => setModalOpen(false)}
    >
      Cancel
    </Button>
  </ModalFooter>
</Modal>
      </Container>
    </div>
  );
};

export default ManageRoomTypes;